'use client';
import { useEffect, useState } from 'react';
import { getInitialVersion, setInitialVersion } from '@/helper/versionStore';

// =========================================================
// Version polling hook
// =========================================================
//
// Detects when a new version of the app has been deployed while the
// user's browser session is still running the old one. The hook is
// wired up once, near the root of the tree; the returned
// `updateAvailable` flag is consumed by the update banner component.
//
// Design decisions worth noting for future readers:
//
//   - Detection only, no reload. The user initiates the reload by
//     tapping the banner. Auto-reloading while an operator is mid-
//     workflow would be disruptive and error-prone.
//
//   - False-positive safety. A failed fetch never flips the flag;
//     only a successful response with a *different* non-empty
//     release does. This matters for mobile MDAs that frequently
//     wander out of wifi coverage — we must not confuse "I can't
//     reach the server right now" with "a new version is live".
//
//   - Cache-busting is belt-and-suspenders. `cache: 'no-store'` +
//     server-side no-store headers are already enough in theory;
//     appending `?t=${Date.now()}` makes caching physically
//     impossible under any intermediary that keys on URL (all of
//     them). Free insurance against future misconfigurations.
//
//   - Multiple triggers. 30s timer covers the idle case; window
//     `online` event covers reconnects; document `visibilitychange`
//     covers tab re-focus after the timer has been throttled in
//     the background; `pageshow` with `event.persisted` covers
//     restoration from the browser's back/forward cache, where
//     in-memory React state is restored wholesale and neither the
//     timer nor visibilitychange reliably fires.
//
//   - Once flipped, stop. `updateAvailable === true` is terminal;
//     further timers and listeners are torn down to avoid wasted
//     work on a session that's just waiting for the user to reload.

const DEFAULT_INTERVAL_MS = 30_000;
const FETCH_TIMEOUT_MS = 5_000;

export interface VersionPollResult {
  updateAvailable: boolean;
}

// Runtime extraction of the `release` string from an arbitrary JSON
// payload. Uses narrowing (no type assertions) so ESLint's
// `no-type-assertion` rule stays happy and a malformed response
// cannot cause a confident-but-wrong read.
const extractRelease = (parsed: unknown): string => {
  if (typeof parsed !== 'object' || parsed === null) {
    return '';
  }
  if (!('release' in parsed)) {
    return '';
  }
  const release = parsed.release;
  return typeof release === 'string' ? release : '';
};

export function useVersionPoll(endpoint: string, intervalMs: number = DEFAULT_INTERVAL_MS): VersionPollResult {
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);

  useEffect(() => {
    if (updateAvailable) {
      // Terminal state — nothing left to do. The banner is visible,
      // the user will reload, and the captured initialVersion is now
      // meaningless.
      return;
    }

    let cancelled = false;

    // Attempts one fetch. Updates the `initialVersion` store on the
    // very first successful response, and flips `updateAvailable` on
    // any subsequent success that sees a different non-empty value.
    // Returns early on any failure — a failed poll is explicitly not
    // evidence of a deploy.
    const probe = async (): Promise<void> => {
      if (cancelled) {
        return;
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort();
      }, FETCH_TIMEOUT_MS);

      try {
        // Cache-buster query param on top of fetch-layer no-store.
        const separator = endpoint.includes('?') ? '&' : '?';
        const bustedUrl = `${endpoint}${separator}t=${Date.now()}`;

        const response = await fetch(bustedUrl, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          return;
        }

        const release = extractRelease(await response.json());
        if (release === '') {
          return;
        }

        const baseline = getInitialVersion();
        if (baseline === null) {
          // First successful response: establish the session baseline.
          setInitialVersion(release);
          return;
        }
        if (release !== baseline) {
          setUpdateAvailable(true);
        }
      } catch {
        // Swallowed deliberately. Transient network failures,
        // aborted timeouts, JSON parse errors — none of these are
        // evidence of a new deploy, so we silently retry on the
        // next tick.
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    // Kick off the first probe immediately on mount so the baseline
    // is established as early as possible.
    void probe();

    const timerId = window.setInterval(() => {
      void probe();
    }, intervalMs);

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void probe();
      }
    };
    const handleOnline = (): void => {
      void probe();
    };
    const handlePageShow = (event: PageTransitionEvent): void => {
      // `persisted` is true when the page is being restored from the
      // browser's back/forward cache. In-memory React state comes
      // back wholesale and no other trigger fires reliably.
      if (event.persisted) {
        void probe();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      cancelled = true;
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [endpoint, intervalMs, updateAvailable]);

  return { updateAvailable };
}
